import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { loadSettings, saveSettings } from '../services/api';
import { HudSettings, defaultWidgets } from '../services/types';

export default function Settings() {
  const [settings, setSettings] = useState<HudSettings>(loadSettings());
  const [saved, setSaved] = useState(false);

  // Load settings on mount
  useEffect(() => {
    setSettings(loadSettings());
  }, []);

  // Update a setting
  const updateSetting = <K extends keyof HudSettings>(key: K, value: HudSettings[K]) => {
    const newSettings = { ...settings, [key]: value };
    setSettings(newSettings);
    saveSettings(newSettings);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  // Update widget URL
  const updateWidgetUrl = (widgetId: string, field: 'api' | 'drillDown', value: string) => {
    const newUrls = {
      ...settings.widgetUrls,
      [widgetId]: {
        ...settings.widgetUrls[widgetId],
        [field]: value,
      },
    };
    updateSetting('widgetUrls', newUrls);
  };

  return (
    <div className="settings-page">
      <Link to="/" className="back-link">← Back to Dashboard</Link>
      <h2>HUD Settings</h2>

      {saved && (
        <div className="status-banner status-healthy" style={{ marginBottom: '1rem' }}>
          ✓ Settings saved
        </div>
      )}

      {/* Refresh Settings */}
      <section className="settings-section">
        <h3>Refresh Interval</h3>
        <p>How often should the dashboard automatically refresh data from widgets?</p>
        <div className="settings-row">
          <span className="settings-label">Auto-refresh interval</span>
          <select
            className="settings-select"
            value={settings.refreshInterval}
            onChange={(e) => updateSetting('refreshInterval', Number(e.target.value))}
          >
            <option value={30}>30 seconds</option>
            <option value={60}>1 minute</option>
            <option value={120}>2 minutes</option>
            <option value={300}>5 minutes</option>
          </select>
        </div>
      </section>

      {/* Drill-Down Behavior */}
      <section className="settings-section">
        <h3>Drill-Down Behavior</h3>
        <p>How should tools open when you click on a widget?</p>
        <div className="settings-row">
          <label className="settings-checkbox">
            <input
              type="checkbox"
              checked={settings.openInNewWindow}
              onChange={(e) => updateSetting('openInNewWindow', e.target.checked)}
            />
            Open tools in new window (recommended for multi-monitor setups)
          </label>
        </div>
      </section>

      {/* Widget URLs */}
      <section className="settings-section">
        <h3>Widget URLs</h3>
        <p>Configure the API and dashboard URLs for each tool. Leave blank to use defaults or disable.</p>

        {defaultWidgets.map(widget => (
          <div key={widget.id} className="widget-settings">
            <label>{widget.icon} {widget.name}</label>
            <small>API endpoint for widget data</small>
            <input
              type="text"
              className="widget-url-input"
              placeholder={widget.id === 'serverhealth'
                ? '/api/proxy/serverhealth (secure proxy)'
                : `https://your-${widget.id}-app.azurestaticapps.net/api/widget-summary`}
              value={settings.widgetUrls[widget.id]?.api || ''}
              onChange={(e) => updateWidgetUrl(widget.id, 'api', e.target.value)}
            />
            {widget.id === 'serverhealth' && (
              <small style={{ color: 'var(--accent-green)' }}>
                Using secure server-side proxy (API key stored in Azure)
              </small>
            )}
            <small>Dashboard URL (opens when widget is clicked)</small>
            <input
              type="text"
              className="widget-url-input"
              placeholder={`https://your-${widget.id}-app.azurestaticapps.net`}
              value={settings.widgetUrls[widget.id]?.drillDown || ''}
              onChange={(e) => updateWidgetUrl(widget.id, 'drillDown', e.target.value)}
            />
          </div>
        ))}

        <div className="settings-actions">
          <span className={`save-status ${saved ? 'visible' : ''}`}>✓ Settings saved automatically</span>
          <Link to="/" className="button-primary">Back to Dashboard</Link>
        </div>
      </section>

      {/* About */}
      <section className="settings-section">
        <h3>About UMT HUD</h3>
        <p>
          UMT HUD (Unified Dashboard) is a mission control dashboard that aggregates
          status widgets from all UMT tools into a single view.
        </p>
        <div className="settings-row">
          <span className="settings-label">Version</span>
          <span>1.4.15</span>
        </div>
        <div className="settings-row">
          <span className="settings-label">Design System</span>
          <span>UMT Design System v1.0</span>
        </div>
        <div className="settings-row">
          <span className="settings-label">Changelog</span>
          <a
            href="https://github.com/UMTJG00d3/UMT-HUD/blob/master/CHANGELOG.md"
            target="_blank"
            rel="noopener noreferrer"
            className="changelog-link"
          >
            View Changelog →
          </a>
        </div>
      </section>
    </div>
  );
}
